import { IS_BYPASS_MODE } from '../constants';

import {
  type AuthedRequestOptions,
  authTokenMissingError,
  fetchWithAuthRecovery,
  isReportedAuthError,
  readErrorBody,
  serverMessageOf,
} from './authRecovery';
import { clientVersionHeaders } from './clientVersion';
import { getAuthToken } from './getAuthToken';

const isNoisyExpectedFetchError = (error: unknown): boolean => {
  // Auth failures carry their own dedicated, deduped telemetry — a console.error
  // per failing endpoint on top of that is pure noise.
  if (isReportedAuthError(error)) {
    return true;
  }

  if (error && typeof error === 'object') {
    const maybeErr = error as { name?: unknown; message?: unknown };
    const name = typeof maybeErr.name === 'string' ? maybeErr.name : '';
    const message =
      typeof maybeErr.message === 'string' ? maybeErr.message : '';

    // Browser-aborted requests (navigation, timeouts, cancelation)
    if (
      name === 'AbortError' ||
      /AbortError|signal is aborted|Fetch is aborted|The user aborted a request/i.test(
        message,
      )
    ) {
      return true;
    }

    // Transient network failures
    if (/Failed to fetch|NetworkError|Load failed/i.test(message)) {
      return true;
    }
  }

  return false;
};

/**
 * One request carrying the session, and NOTHING else — no body parsing, no status
 * checking. The single place the bypass short-circuit and the token acquisition
 * live, so a second caller cannot grow a second auth path.
 */
const authedFetch = async (
  url: string,
  options?: AuthedRequestOptions,
): Promise<Response> => {
  if (IS_BYPASS_MODE) {
    // Bypass short-circuits the whole auth path: no token, so no refresh, no
    // re-auth and no auth telemetry can be triggered from here. The build version
    // still rides along, so a local request has the same shape as a deployed one —
    // which also means a backend checkout predating TMDXNC-362's `allowedHeaders`
    // fails the preflight here. The fix for that is to pull the backend, not to
    // drop the header: a loud, immediate failure beats a divergent request shape.
    return fetch(url, {
      method: options?.method ?? 'GET',
      headers: {
        ...clientVersionHeaders(),
        ...(options?.json === undefined
          ? {}
          : { ['Content-Type']: 'application/json' }),
      },
      ...(options?.json === undefined
        ? {}
        : { body: JSON.stringify(options.json) }),
      signal: options?.signal,
    });
  }

  const token = await getAuthToken();
  if (!token) {
    // Either the expected pre-login state or a session that vanished mid-load —
    // only `authTokenMissingError` can tell those apart, and the difference
    // decides whether anything re-authenticates.
    throw authTokenMissingError();
  }
  return fetchWithAuthRecovery(url, token, options);
};

export const fetchWithAuthHeaders = async (
  url: string,
  json = true,
  options?: { signal?: AbortSignal },
) => {
  try {
    const res = await authedFetch(url, options);

    if (json) {
      // Safe with respect to auth: `fetchWithAuthRecovery` never returns a 401 or
      // 403. NOT safe in general — a 5xx error page still reaches here and will
      // throw a SyntaxError, which is pre-existing behaviour this change does not
      // address.
      const rawData = await res.json();

      return rawData;
    }
    return res;
  } catch (error) {
    // Avoid noisy console.error logs for expected client/network states.
    if (!isNoisyExpectedFetchError(error)) {
      console.error('Fetch Error:', error);
    }
    throw error;
  }
};

/**
 * A non-2xx reply, carrying the status and whatever the server said about it.
 *
 * Distinct from `fetchWithAuthHeaders`, which parses whatever a non-ok response
 * holds and hands it back as data — so a Nest
 * `{ statusCode: 404, message: 'unknown template "x"' }` arrives at the caller as
 * a successful result of the wrong shape. For the read endpoints that is
 * pre-existing; for a write it would report a fabricated event as fired.
 *
 * Not exported: the base query and `asQueryFnResult` flatten a thrown error to a
 * plain `{ error, name }` long before anything can catch it, so `instanceof` is
 * gone by then — the same reason `AUTH_ERROR_NAME` exists.
 */
class RequestFailedError extends Error {
  override readonly name = 'RequestFailedError';

  readonly status: number;

  readonly body: unknown;

  constructor(status: number, detail: string | null, body: unknown) {
    super(detail === null ? `Request failed (HTTP ${status}).` : detail);
    this.status = status;
    this.body = body;
  }
}

/**
 * One authenticated JSON request whose status is actually checked, for callers
 * that must not mistake an error body for a result.
 *
 * Shares `authedFetch` with `fetchWithAuthHeaders`, so a POST gets the same 401
 * refresh-and-retry and the same 403 handling rather than a second, thinner copy
 * of the auth logic.
 */
export const requestJsonWithAuth = async <T>(
  url: string,
  options?: AuthedRequestOptions,
): Promise<T> => {
  const res = await authedFetch(url, options);

  if (!res.ok) {
    const body = await readErrorBody(res);
    throw new RequestFailedError(res.status, serverMessageOf(body), body);
  }

  return (await res.json()) as T;
};
